import { parseDateFromISO, addDays, getToday } from '@applicator/shared'

import { supabase } from '../../lib/supabase'

import { isTimestampField, getDbColumnForField, formatDateForDatabase } from './helpers'
import { DateFieldConfig, JobDatesData, DateMode, UserRules } from './types'

// Update job date field
export async function updateJobDateField(
  fieldKey: string,
  mode: DateMode,
  manualValue: string | null,
  jobId: string,
  jobDates: JobDatesData,
  userRules: UserRules,
  fieldConfigs: DateFieldConfig[],
  setJobDates: React.Dispatch<React.SetStateAction<JobDatesData>>,
  onUpdated?: (updatedDates: Partial<JobDatesData>) => void
) {
  try {
    if (!jobId) {
      throw new Error('Job ID is required to update date field')
    }
    
    const modeColumn = `${fieldKey}_mode`
    const manualColumn = `${fieldKey}_manual`
    const dbColumn = getDbColumnForField(fieldKey)

    const updateData: Record<string, unknown> = {
      [modeColumn]: mode,
      [manualColumn]: manualValue,
    }

    // If mode is 'manual' and we have a value, also update the main field
    // For 'cleared' or 'auto', set main field to null (will be computed)
    if (mode === 'manual' && manualValue) {
      updateData[dbColumn] = manualValue
    } else if (mode === 'cleared') {
      updateData[dbColumn] = null
    } else if (mode === 'auto') {
      // For auto mode, compute the effective date and update main field
      const effectiveDate = fieldConfigs.find(f => f.key === fieldKey)?.getAutoDate(jobDates, userRules)
      if (effectiveDate) {
        updateData[dbColumn] = formatDateForDatabase(effectiveDate, fieldKey)
      } else {
        updateData[dbColumn] = null
      }
    }

    const { error } = await supabase
      .from('jobs')
      .update(updateData)
      .eq('id', jobId)

    if (error) throw error

    // Update local state
    const updatedDates = {
      ...jobDates,
      [modeColumn]: mode,
      [manualColumn]: manualValue,
      // Also update the main field if it was updated
      ...(dbColumn && updateData[dbColumn] !== undefined ? { [dbColumn]: updateData[dbColumn] } : {}),
    }

    setJobDates(updatedDates)
    onUpdated?.(updatedDates)
  } catch (error) {
    console.error('Failed to update date field:', error)
    alert('Failed to update date. Please try again.')
  }
}

// Handle date selection from picker
export async function handleDateSelect(
  fieldKey: string,
  dateStr: string,
  jobId: string,
  jobDates: JobDatesData,
  userRules: UserRules,
  fieldConfigs: DateFieldConfig[],
  setJobDates: React.Dispatch<React.SetStateAction<JobDatesData>>,
  setOpenField: React.Dispatch<React.SetStateAction<string | null>>,
  onUpdated?: (updatedDates: Partial<JobDatesData>) => void
) {
  if (!jobId) {
    console.error('Cannot update date field: jobId is missing')
    return
  }
  if (typeof setOpenField !== 'function') {
    console.error('Cannot update date field: setOpenField is not a function')
    return
  }
  
  try {
    const date = parseDateFromISO(dateStr)
    const dbValue = formatDateForDatabase(date, fieldKey)
    await updateJobDateField(fieldKey, 'manual', dbValue, jobId, jobDates, userRules, fieldConfigs, setJobDates, onUpdated)
    setOpenField(null)
  } catch (error) {
    console.error('Error in handleDateSelect:', error)
    // Don't close the picker if there was an error
  }
}

// Handle quick actions
export async function handleQuickAction(
  fieldKey: string,
  action: string,
  jobId: string,
  jobDates: JobDatesData,
  userRules: UserRules,
  fieldConfigs: DateFieldConfig[],
  setJobDates: React.Dispatch<React.SetStateAction<JobDatesData>>,
  setOpenField: React.Dispatch<React.SetStateAction<string | null>>,
  onUpdated?: (updatedDates: Partial<JobDatesData>) => void
) {
  const today = getToday()
  let date: Date | null = null
  let mode: DateMode = 'manual'

  switch (action) {
    case 'today':
      // For TIMESTAMPTZ fields, use current time; for DATE fields, use midnight
      if (isTimestampField(fieldKey)) {
        date = new Date() // Current date and time
      } else {
        date = today // Midnight
      }
      break
    case '+3d':
      date = addDays(today, 3)
      break
    case '+7d':
      date = addDays(today, 7)
      break
    case 'clear':
      mode = 'cleared'
      break
    case 'revert':
      mode = 'auto'
      break
  }

  const dbValue = formatDateForDatabase(date, fieldKey)
  await updateJobDateField(fieldKey, mode, dbValue, jobId, jobDates, userRules, fieldConfigs, setJobDates, onUpdated)
  setOpenField(null)
}
